import React from 'react';
import {NextSeo} from 'next-seo';
import {Footer, Navbar} from '../../components';

import Link from 'next/link';
import Image from 'next/image';
import Dries from '../../../public/assets/dries.png';
import Carlo from '../../../public/assets/carlo.png';
import dayjs from 'dayjs';
import {GetStaticProps, InferGetStaticPropsType} from 'next';
import Head from 'next/head';
import {getPosts} from '../../lib/ghost';
import {Author, PostOrPage} from '@tryghost/content-api';

/**
 *
 * @param root0
 * @param root0.posts
 */
export default function Home({posts}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <>
      <NextSeo
        title={'Plunk Blog | The Open-Source Email Platform'}
        description={'Tips, Tricks and Guides about Plunk and everything email'}
        openGraph={{
          title: 'Plunk Blog | The Open-Source Email Platform',
          description: 'Tips, Tricks and Guides about Plunk and everything email',
          images: [{url: 'https://www.useplunk.com/assets/card.png', alt: 'Plunk Blog'}],
        }}
        additionalMetaTags={[
          {
            property: 'title',
            content: 'Plunk Blog | The Open-Source Email Platform',
          },
        ]}
      />

      <Head>
        <link rel="alternate" type="application/rss+xml" href="https://www.useplunk.com/rss/blog/feed.xml" />
        <link rel="alternate" type="application/rss+json" href="https://www.useplunk.com/rss/blog/feed.json" />
      </Head>

      <Navbar />

      <div className={'mx-auto max-w-7xl px-8 sm:px-0'}>
        <main>
          <div className="relative mx-auto max-w-lg space-y-12 lg:max-w-4xl">
            <div className={'flex flex-col items-center justify-center gap-y-2'}>
              <div className={'mt-9 space-y-2 text-center'}>
                <h1 className="text-3xl font-bold tracking-tight text-neutral-800 sm:text-5xl">Plunk Blog</h1>
                <p className="mx-auto max-w-lg text-lg text-neutral-500">
                  Tips, Tricks and Guides about Plunk and everything email
                </p>
              </div>
            </div>
            <div className="grid gap-16 pt-12 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-24">
              {posts.map(post => (
                <Link href={`/blog/${post.slug}`} key={post.slug}>
                  <Image
                    width={1200}
                    height={627}
                    placeholder={'blur'}
                    blurDataURL={`/_next/image?url=${post.feature_image}&w=16&q=1`}
                    className="rounded-md border border-neutral-200 object-contain"
                    src={post.feature_image ?? '/assets/card.png'}
                    alt={post.feature_image_alt ?? 'Cover Image'}
                  />

                  <div className="space-y-3 py-6">
                    <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-800">
                      {dayjs(post.published_at).format('DD MMM YYYY')}
                    </span>

                    <div className="min-h-24 space-y-3">
                      <p className="h-1/2 text-xl font-semibold text-neutral-800">{post.title}</p>
                      <p className="text-sm text-neutral-500">{post.excerpt}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div>
                      <span className="sr-only">{post.primary_author.name}</span>
                      <Image
                        className="h-10 w-10 rounded-full"
                        src={post.primary_author.name === 'Dries Augustyns' ? Dries : Carlo}
                        alt={post.primary_author.name === 'Dries Augustyns' ? 'Dries Augustyns' : "Carlo D'Agnolo"}
                      />
                    </div>
                    <div className={'text-sm'}>
                      <p className="font-medium text-neutral-800">{post.primary_author.name}</p>
                      <p className="text-neutral-500">
                        {post.primary_author.name === 'Dries Augustyns' ? 'Technical Lead' : 'Head of Growth'} at Plunk
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{posts: (PostOrPage & {primary_author: Author})[]}> = async () => {
  const posts = (await getPosts()) as (PostOrPage & {primary_author: Author})[];

  return {
    props: {posts},
    revalidate: 3600,
  };
};
